import {
  ActionIcon,
  Badge,
  Box,
  Group,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { openContextModal } from "@mantine/modals";
import { IconPencil } from "@tabler/icons-react";
import { useTranslation } from "next-i18next";
import { CircleFlag } from "react-circle-flags";
import { Member } from "../../common/members/member.schema";
import {
  SeedType,
  Event,
  TournamentType,
} from "../../common/events/event.schema";
import { getShowClubs, getShowCountries } from "../../common/util";
import Hider from "../common/Hider";
import MemberDisplay from "../members/MemberDisplay";
import { getHasPools, getHasTeams } from "./util";
import { trackEvent } from "../../common/analytics";
import { Weapon } from "../../common/sports";

const SeedTable = ({
  event,
  link,
  editable,
}: {
  event: Event;
  link?: boolean;
  editable?: boolean;
}) => {
  const hasTeams = getHasTeams(event);
  // const hasPools = getHasPools(event);
  // const showTeams = hasTeams && (!hasPools || event.poolsComplete);
  let showTeams = hasTeams;
  if (
    event.type === TournamentType.TEAM_POOLS_ELIMINATION_SPLIT &&
    !event.poolsComplete
  )
    showTeams = false;
  let activeSeeds = event.seeds.filter(
    (m) => event.withdrawals.indexOf(m) === -1
  );
  if (event.eliminationSeeds.length) {
    activeSeeds = event.eliminationSeeds.filter(
      (m) => event.withdrawals.indexOf(m) === -1
    );
  }
  const { t } = useTranslation();
  const members = event.members as Member[];
  const teams = event.teams as Member[];
  const hasMultiplePools = getHasPools(event) && event.pools.length > 1;
  const openEditTeam = (team: Member) => {
    // console.log("openEditTeam :", team);
    trackEvent("open_modal_edit_team", { board: event.board });
    openContextModal({
      title: t("Edit team"),
      modal: "editTeam",
      innerProps: {
        team,
      },
    });
  };
  // console.log('showTeams :', showTeams);
  const showPools = hasMultiplePools;
  const showClubs = getShowClubs(event.members as Member[]);
  const showCountries = getShowCountries(event.members as Member[]);
  const poolCutoff = event.eliminations.length && event.eliminations[0].size;
  // console.log('poolCutoff :', poolCutoff);
  return (
    <Table fz="xs">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>
            <Hider smallerThan="xs">
              <Box>Seed</Box>
            </Hider>
          </Table.Th>
          <Table.Th>{showTeams ? "Team" : "Name"}</Table.Th>
          {event.seedType === SeedType.SCORE && !showTeams && (
            <Table.Th>Score</Table.Th>
          )}
          {showClubs && (
            <Hider smallerThan="sm">
              <Table.Th>
                <Box>Club</Box>
              </Table.Th>
            </Hider>
          )}
          {showCountries && (
            <Table.Th>
              <Hider smallerThan="xs">
                <Box>Country</Box>
              </Hider>
            </Table.Th>
          )}
          {event.seedType === SeedType.USFA && !showTeams && (
            <Table.Th>
              <Hider smallerThan="xs">
                <Box>Rating</Box>
              </Hider>
            </Table.Th>
          )}
          {showPools && !showTeams && <Table.Th>Pool</Table.Th>}
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {activeSeeds.map((s, i) => {
          // console.log('showTeams :', showTeams);
          const member = showTeams
            ? (teams.find((m) => m._id === s) as Member)
            : (members.find((m) => m._id === s) as Member);
          //   console.log("member :", member);
          if (!member) return null;
          const pool = event.pools.find((p) =>
            p.members.find((pm) => pm.member === s)
          );
          const poolIndex = pool ? event.pools.indexOf(pool) : 0;
          let rating = "U";
          //   console.log("rating :", rating);
          //   console.log("event.weapon :", event.weapon);
          if (event.weapon === Weapon.FOIL && member.usfaRatingFoil)
            rating = member.usfaRatingFoil;
          if (event.weapon === Weapon.EPEE && member.usfaRatingEpee)
            rating = member.usfaRatingEpee;
          if (event.weapon === Weapon.SABRE && member.usfaRatingSabre)
            rating = member.usfaRatingSabre;
          return (
            <Table.Tr key={s} style={{ pageBreakInside: "avoid" }}>
              <Table.Td>{i + 1}</Table.Td>
              <Table.Td>
                {showTeams && (
                  <Stack pt={10}>
                    <Group>
                      <MemberDisplay size="sm" member={member} avatar />
                      {editable && (
                        <ActionIcon
                          onClick={() => openEditTeam(member)}
                          color="primary"
                        >
                          <IconPencil size={14} />
                        </ActionIcon>
                      )}
                      {event.poolsComplete && i >= poolCutoff && (
                        <Badge color="red" size="xs">
                          Eliminated
                        </Badge>
                      )}
                    </Group>
                    <Stack gap={5} mb={20}>
                      {member.members?.map((tm) => {
                        const member = members.find(
                          (m) => m._id === tm
                        ) as Member;
                        if (!member) return null;
                        return (
                          <MemberDisplay
                            key={member._id}
                            size="xs"
                            member={member}
                            avatar
                            link={link}
                          />
                        );
                      })}
                    </Stack>
                  </Stack>
                )}
                {!showTeams && (
                  <Stack gap={5}>
                    <Group>
                      <MemberDisplay
                        size="sm"
                        member={member}
                        avatar
                        link={link}
                      />
                      {event.poolsComplete && i >= poolCutoff && (
                        <Badge color="red" size="xs">
                          Eliminated
                        </Badge>
                      )}
                    </Group>
                    <Hider largerThan="sm">
                      <Box ml={42}>
                        <Text size="xs">{member.club}</Text>
                      </Box>
                    </Hider>
                  </Stack>
                )}
              </Table.Td>
              {event.seedType === SeedType.SCORE && !showTeams && (
                <Table.Td>{member.score}</Table.Td>
              )}
              {showClubs && (
                <Hider smallerThan="sm">
                  <Table.Td>{member.club}</Table.Td>
                </Hider>
              )}
              {showCountries && (
                <Table.Td>
                  {member.country && (
                    <CircleFlag
                      countryCode={member.country.toLowerCase()}
                      height={20}
                    />
                  )}
                </Table.Td>
              )}
              {event.seedType === SeedType.USFA && !showTeams && (
                <Table.Td>{rating}</Table.Td>
              )}
              {showPools && !showTeams && <Table.Td>{poolIndex + 1}</Table.Td>}
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
};
export default SeedTable;
